import React, { useState, useEffect, useRef } from 'react';
import './pages.css';

const About = () => {
  const [activeTimeline, setActiveTimeline] = useState(0);
  const [animatedNumbers, setAnimatedNumbers] = useState({
    projects: 0,
    clients: 0,
    years: 0,
    countries: 0
  });
  const [isNumbersVisible, setIsNumbersVisible] = useState(false);
  const numbersRef = useRef(null);
  const animationIntervals = useRef({});

  // Animate numbers when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Reset numbers to 0 and trigger animation every time
          setAnimatedNumbers({
            projects: 0,
            clients: 0,
            years: 0,
            countries: 0
          });
          setIsNumbersVisible(true);
          // Small delay to ensure state is reset before animation starts
          setTimeout(() => {
            animateNumbers();
          }, 100);
        } else {
          // Reset visibility state when element is out of view
          setIsNumbersVisible(false);
        }
      },
      { threshold: 0.3 }
    );

    if (numbersRef.current) {
      observer.observe(numbersRef.current);
    }

    return () => {
      if (numbersRef.current) {
        observer.unobserve(numbersRef.current);
      }
    };
  }, []); // Remove isNumbersVisible dependency to allow multiple triggers

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      Object.values(animationIntervals.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  const animateNumbers = () => {
    // Clear any existing animation intervals
    Object.values(animationIntervals.current).forEach(interval => {
      if (interval) clearInterval(interval);
    });
    animationIntervals.current = {};

    const targetNumbers = {
      projects: 500,
      clients: 150,
      years: 19,
      countries: 25
    };

    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    Object.keys(targetNumbers).forEach(key => {
      let current = 0;
      const increment = targetNumbers[key] / steps;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= targetNumbers[key]) {
          current = targetNumbers[key];
          clearInterval(timer);
          // Remove from intervals ref when animation completes
          delete animationIntervals.current[key];
        }
        setAnimatedNumbers(prev => ({
          ...prev,
          [key]: Math.floor(current)
        }));
      }, stepDuration);
      
      // Store interval reference for cleanup
      animationIntervals.current[key] = timer;
    });
  };

  return (
    <div className="about-container">
      {/* Hero Section */}
      <div className="about-hero">
        <div className="about-hero-content">
          <h1>So how did <span className="highlight"></span> come to be?</h1>
          <p>
          We found our momentum at the crossroads of ideas and action—where insights spark collaboration, conversations drive engagement, and growth happens fast. All powered by transparency and the freedom to work our way.
          </p>
        </div>
      </div>

      {/* It's all about the people */}
      <div className="about-people">
        <div className="about-people-content">
          <h2>It's all <br/> about <br/> the <br/> people</h2>
          <p>
          </p>
        </div>
      </div>

      {/* Our Story Timeline */}
      <div className="about-story">
        <h2>Our story</h2>
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-year">2005</div>
            <div className="timeline-content">
              <h3>RDV Systems is founded</h3>
              <p>
                Our founders experienced firsthand the challenges that come with 
                quickly scaling infrastructure projects. They decided to build a 
                solution that people actually love to use - and so RDV Systems was born!
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2007</div>
            <div className="timeline-content">
              <h3>Planting the seed</h3>
              <p>
                Secured our first major contract with a state DOT, proving our 
                innovative approach to 3D visualization and safety analysis.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2010</div>
            <div className="timeline-content">
              <h3>We're onto something...</h3>
              <p>
                The RDV Systems team discovered deep, shared pain points across 
                infrastructure teams around the world and built a prototype for 
                what is known today as our Road Safety Audit 3D technology.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2012</div>
            <div className="timeline-content">
              <h3>Our first customers!</h3>
              <p>
                After years of development, RDV Systems officially launched and 
                hosted its first major project after onboarding our first 10 
                state DOT customers.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2015</div>
            <div className="timeline-content">
              <h3>Off we grow</h3>
              <p>
                Grew the team by 200% by hiring new employees and grew the company 
                by 500% by acquiring over 25 major infrastructure customers.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2018</div>
            <div className="timeline-content">
              <h3>Expanding our crew</h3>
              <p>
                Secured major partnerships and acquired 50+ customers across 
                multiple states and countries.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2020</div>
            <div className="timeline-content">
              <h3>Welcome to 2.0</h3>
              <p>
                Launched RDV 2.0, our transformation into a comprehensive work 
                operating system that enables organizations of any size to build 
                custom visualization apps in minutes.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2022</div>
            <div className="timeline-content">
              <h3>123BIM Revolution</h3>
              <p>
                Introduced 123BIM, our revolutionary visualization platform that 
                seamlessly converts complex project designs into 3D digital models 
                for cloud collaboration and public engagement.
              </p>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-year">2024</div>
            <div className="timeline-content">
              <h3>AI-Powered Future</h3>
              <p>
                With advanced AI integration, we introduced our next generation 
                of capabilities, helping teams move beyond traditional visualization 
                into true intelligent infrastructure analysis.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RDV Systems by the numbers */}
      <div className={`about-numbers ${isNumbersVisible ? 'visible' : ''}`} ref={numbersRef}>
        
        <div className="numbers-grid">
        <h2>RDV Systems by the numbers</h2>
        <div className="numbers-grid-items">
          <div className={`number-item ${isNumbersVisible ? 'animate' : ''}`}>
            <div className="number">{animatedNumbers.projects}+</div>
            <div className="number-label">Projects Completed</div>
          </div>
          <div className={`number-item ${isNumbersVisible ? 'animate' : ''}`}>
            <div className="number">{animatedNumbers.clients}+</div>
            <div className="number-label">Happy Clients</div>
          </div>
          <div className={`number-item ${isNumbersVisible ? 'animate' : ''}`}>
            <div className="number">{animatedNumbers.years}</div>
            <div className="number-label">Years of Experience</div>
          </div>
          <div className={`number-item ${isNumbersVisible ? 'animate' : ''}`}>
            <div className="number">{animatedNumbers.countries}+</div>
            <div className="number-label">Countries Served</div>
          </div>
        </div>
        </div>
      </div>

      {/* Our culture and values */}
      <div className="about-values">
        <h2>Our culture and values</h2>
        <div className="values-grid">
          <div className="value-item">
            <h3>Innovation and Excellence</h3>
            <p>
              Data is at the core of our identity both as a company and a team, 
              and is an essential part of our platform. Access to data allows 
              teams to work even better together and perform at their professional best.
            </p>
          </div>
          <div className="value-item">
            <h3>Ownership and Impact</h3>
            <p>
              We have learned that our employees need to take full ownership over 
              their work, from A to Z, which instills a sense of personal investment 
              and drive leading to better overall execution.
            </p>
          </div>
          <div className="value-item">
            <h3>Speed and Execution</h3>
            <p>
              We believe the faster you execute, the faster you learn, iterate, 
              and improve. We are constantly running at our own pace and seeking 
              feedback to outdo our best.
            </p>
          </div>
          <div className="value-item">
            <h3>Product-first</h3>
            <p>
              We mean it when we say that a product should work for the customer 
              and not the other way around. Every feature is designed with ease 
              of use in mind to deliver the best user experience.
            </p>
          </div>
          <div className="value-item">
            <h3>Safety-Centricity</h3>
            <p>
              Our customers' safety and success is at the heart of everything we do. 
              We are passionately committed to helping customers with our best-in-class 
              support for safer infrastructure.
            </p>
          </div>
          <div className="value-item">
            <h3>Transparency and Trust</h3>
            <p>
              We have embedded transparency in the core of our company values and 
              our platform experience, providing an experience open to everyone 
              in the infrastructure community.
            </p>
          </div>
        </div>
      </div>

      {/* Mission and Vision */}
      <div className="about-mission">
        <div className="mission-content">
          <h2>Together we can impact how infrastructure teams work across the globe</h2>
          <div className="mission-statement">
            <div className="mission-item">
              <h3>MISSION</h3>
              <p>To lead the industry with a streamlined 3D real-time, visual communication solution.</p>
            </div>
            <div className="mission-item">
              <h3>VISION</h3>
              <p>To provide a globally cost-effective tool for project visualization and road safety that dramatically expands cooperation and communication for teams and the public.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="about-cta">
        <h2>Empowering teams to accomplish more, together</h2>
        <p>Join the 50+ State DOTs and agencies that trust RDV Systems for their visualization and safety needs.</p>
        <div className="cta-buttons">
          <a href="/contact" className="cta-button primary">Get Started</a>
          <a href="/services" className="cta-button secondary">Learn More</a>
        </div>
      </div>
    </div>
  );
};

export default About;
